// The regions screen's query state, as URL search params.
//
// Same reasoning as `billingSearch.ts` and `dealsSearch.ts`: "our Canadian
// revenue by province" is a link somebody sends, and a report whose
// controls live only in component state cannot be one.
//
// It follows `billingSearch.ts`'s hardened shape rather than the older one it
// replaced, and for the same two reasons: every field carries its own
// `.catch(undefined)` so one unreadable param falls back alone, and the parse
// runs AGAIN in `searchToRegionState` at the point of use, because TanStack
// Router merges each route's validated search over its parent's and the root
// route validates nothing. A `?group_by=city` that this schema rejected still
// reaches the component, and without the second parse it would reach the API —
// where it is a 400, on a screen that should have shown the country cut.

import { z } from "zod";
import { Currency, ReportScope, RevenueByRegionGroupBy, Uuid } from "@crm/shared";
import { DEFAULT_REGION_QUERY, type RegionQueryState } from "../../api/queries.js";

const RegionsSearchSchema = z.object({
  group_by: RevenueByRegionGroupBy.optional().catch(undefined),
  /**
   * Validated as a uuid here rather than passed through: `?partner_id=harris`
   * is `validation_failed` from the API, and the report is perfectly readable
   * without a partner filter — so the unreadable half is dropped and the rest
   * of the link still works.
   */
  partner_id: Uuid.optional().catch(undefined),
  scope: ReportScope.optional().catch(undefined),
});

export type RegionsSearch = z.infer<typeof RegionsSearchSchema>;

export function validateRegionsSearch(raw: Record<string, unknown>): RegionsSearch {
  const parsed = RegionsSearchSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

export function searchToRegionState(raw: RegionsSearch, currency: Currency): RegionQueryState {
  const search = validateRegionsSearch(raw as Record<string, unknown>);
  return {
    currency,
    groupBy: search.group_by ?? DEFAULT_REGION_QUERY.groupBy,
    partnerId: search.partner_id ?? DEFAULT_REGION_QUERY.partnerId,
    scope: search.scope ?? DEFAULT_REGION_QUERY.scope,
  };
}

/** Defaults are omitted, so the default cut of the report is a clean `/regions`. */
export function regionStateToSearch(state: RegionQueryState): RegionsSearch {
  return {
    ...(state.groupBy === DEFAULT_REGION_QUERY.groupBy ? {} : { group_by: state.groupBy }),
    ...(state.partnerId === null ? {} : { partner_id: state.partnerId }),
    // The scope only means something alongside a partner — the report scopes
    // nothing without one — so writing it into an all-partners URL would be
    // two different links to one identical set of rows.
    ...(state.partnerId === null || state.scope === DEFAULT_REGION_QUERY.scope
      ? {}
      : { scope: state.scope }),
  };
}
